import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Eye, Pause, Play, Users } from 'lucide-react';

import {
  expectedState,
  reviewPolicyOf,
  useAdminStorefront,
  useRefreshStorefront,
  useSetStorefrontReviewPolicy,
  useSetStorefrontStatus,
  useStorefrontPreviewToken,
  type AdminStorefrontDetail,
  type StorefrontExpectedState,
  type StorefrontReviewPolicy,
} from '@/lib/api/storefrontAdminHooks';

import { PendingChangesPanel, RevertButton, VersionsPanel } from './ChangesReview';
import { CopyPanel } from './CopyPanel';
import { OwnerIdentityPanel } from './OwnerIdentityPanel';
import { PayoutsPanel } from './PayoutsPanel';
import { ReviewPanel, isFirstApproval, movedSince } from './ReviewPanel';
import {
  REVIEW_POLICIES,
  ReviewPolicyChooser,
  ReviewPolicyPill,
  ReviewPolicySafetyNote,
  isReviewPolicy,
  reviewPolicyOption,
} from './reviewPolicy';
import { StoreChangeNotice, useUnseenStoreChanges } from './StoreChangeNotice';
import { productNameMap } from './storeChanges';
import { SettingsPanel } from './SettingsPanel';
import { editedSinceApproval as isEditedSinceApproval, liveButNotServed, storeProblems } from './storeCompliance';
import {
  BrandPanel,
  CouponsPanel,
  EventsPanel,
  MetricsPanel,
  ProductsPanel,
  ProductsWarning,
  TeamPanel,
} from './StoreSections';
import {
  REVIEW_LABEL,
  REVIEW_TONE,
  STATUS_LABEL,
  STATUS_TONE,
  STORE_ROOT_DOMAIN,
  count,
  dateTime,
  isRealMedicalGroup,
  refusalMessage,
  safeHttpUrl,
  storeUrl,
} from './storefrontFormat';
import {
  ADMIN_CONTRAST,
  ActionButton,
  ConfirmDialog,
  CopyButton,
  Notice,
  Pill,
  Skeleton,
  StoreMark,
  inputClass,
  textareaClass,
} from './ui';

/** Owners read the pause reason in their notice: a few words at least. */
const MIN_REASON = 5;

export function StorefrontDetailScreen() {
  const { storefrontId } = useParams<{ storefrontId: string }>();
  const { data: store, isLoading, isError, error } = useAdminStorefront(storefrontId);

  return (
    <div className="flex flex-col gap-20" style={ADMIN_CONTRAST}>
      <Link
        to="/admin/storefronts"
        className="inline-flex items-center gap-6 self-start type-label-sm text-ink-muted hover:text-ink"
      >
        <ArrowLeft size={13} aria-hidden /> Storefronts
      </Link>
      {isLoading ? (
        <div className="flex flex-col gap-16">
          <div className="flex items-center gap-14">
            <Skeleton className="w-56 h-56" />
            <div className="flex flex-col gap-8">
              <Skeleton className="h-24 w-[24rem]" />
              <Skeleton className="h-12 w-[16rem]" />
            </div>
          </div>
          <Skeleton className="h-[20rem]" />
        </div>
      ) : isError || !store ? (
        <Notice tone="danger">
          {(error as { code?: string } | null)?.code === 'storefront_not_found'
            ? 'This store does not exist.'
            : `Could not load this store. ${refusalMessage(error)}`}
        </Notice>
      ) : (
        <StoreDetail key={store.id} store={store} />
      )}
    </div>
  );
}

function StoreDetail({ store }: { store: AdminStorefrontDetail }) {
  // The backend's review baseline, as the list reads it: a change that is
  // only waiting for review is not an edit since approval.
  const editedSinceApproval = isEditedSinceApproval(store);
  const edits = store.edits_since_review ?? 0;
  const problems = storeProblems(store);
  const notServed = liveButNotServed(store);
  const unseen = useUnseenStoreChanges(store);
  return (
    <div className="flex flex-col gap-20">
      <header className="flex flex-col gap-16 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-center gap-14 min-w-0 lg:flex-1">
          <StoreMark
            name={store.name}
            logoUrl={store.logo_url}
            accent={store.design?.theme?.primary ?? store.accent_color}
            size={56}
          />
          <div className="flex flex-col gap-8 min-w-0">
            <h1 className="type-headline-lg text-ink truncate">{store.name}</h1>
            <div className="flex flex-wrap items-center gap-6">
              <span className="font-mono text-12 text-ink-secondary">
                {store.slug}.{STORE_ROOT_DOMAIN}
              </span>
              {notServed ? (
                <Pill tone="danger" dot>
                  Live · not served
                </Pill>
              ) : (
                <Pill tone={STATUS_TONE[store.status] ?? 'neutral'} dot>
                  {STATUS_LABEL[store.status] ?? store.status}
                </Pill>
              )}
              <Pill tone={REVIEW_TONE[store.review_status] ?? 'neutral'}>
                {REVIEW_LABEL[store.review_status] ?? store.review_status}
              </Pill>
              {editedSinceApproval && (
                <Pill tone="warning" className="normal-case">
                  Edited since approval{edits > 0 ? ` · ${edits} edit${edits === 1 ? '' : 's'}` : ''}
                </Pill>
              )}
              {!isFirstApproval(store) && <ReviewPolicyPill policy={reviewPolicyOf(store)} />}
            </div>
          </div>
        </div>
        <StoreActions store={store} unseenChanges={unseen.changes.length} />
      </header>

      <StoreChangeNotice
        store={store}
        changes={unseen.changes}
        onAcknowledge={unseen.acknowledge}
      />

      {problems.length > 0 && (
        <Notice tone={problems.some((p) => p.severe) ? 'danger' : 'warning'}>
          <span className="flex flex-col gap-4">
            <strong className="font-normal type-label-sm">
              {problems.length === 1 ? 'This store breaks a store rule' : `This store breaks ${problems.length} store rules`}
            </strong>
            {problems.map((problem) => (
              <span key={problem.code}>{problem.message}</span>
            ))}
            <span className="text-ink-secondary">
              Fix the name, address or medical group under Store settings.
              {notServed
                ? ' Its existing plans keep renewing while the store is not served; decide with the owner whether to rename the store or wind those plans down.'
                : ''}
            </span>
          </span>
        </Notice>
      )}
      <ProductsWarning store={store} />
      <PendingChangesPanel store={store} />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,36rem)] gap-20 items-start">
        <div className="flex flex-col gap-20 min-w-0">
          <ReviewPanel
            key={store.id}
            store={store}
            unseenChanges={unseen.changes.length}
          />
          <CopyPanel store={store} />
          <ProductsPanel store={store} />
          <CouponsPanel store={store} />
          <MetricsPanel store={store} />
          <PayoutsPanel storefrontId={store.id} liveSince={firstLiveAt(store)} />
        </div>
        <div className="flex flex-col gap-20 min-w-0">
          <OwnerIdentityPanel store={store} />
          <div id="store-settings" className="scroll-mt-20">
            <SettingsPanel store={store} />
          </div>
          <BrandPanel store={store} />
          <TeamPanel store={store} />
          <VersionsPanel store={store} />
        </div>
      </div>
      {/* Full width: a save's changes (an FAQ diff, photos) need the room. */}
      <EventsPanel events={store.events} productNames={productNameMap(store)} />
    </div>
  );
}

/** When the store first went live: its first approval, else the review
 *  date. */
function firstLiveAt(store: AdminStorefrontDetail): string | null {
  const first = store.events
    .filter((e) => e.action === 'approved' && Number.isFinite(Date.parse(e.created_at)))
    .map((e) => e.created_at)
    .sort((a, b) => Date.parse(a) - Date.parse(b))[0];
  return first ?? store.reviewed_at ?? null;
}

function StoreActions({
  store,
  unseenChanges,
}: {
  store: AdminStorefrontDetail;
  unseenChanges: number;
}) {
  const preview = useStorefrontPreviewToken(store.id);
  const setStatus = useSetStorefrontStatus(store.id);
  const refresh = useRefreshStorefront(store.id);
  const [snapshot, setSnapshot] = useState<StorefrontExpectedState | null>(null);
  const [previewLink, setPreviewLink] = useState<{ url: string; expires_at: string | null } | null>(
    null,
  );
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [statusDialog, setStatusDialog] = useState<'live' | 'paused' | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  // Kept until a pause is sent, so a refusal never loses what was typed.
  const [pauseReason, setPauseReason] = useState('');
  const pauseReasonOk = pauseReason.trim().length >= MIN_REASON;

  const live = store.status === 'live';
  const served = live && !liveButNotServed(store);
  const paused = store.status === 'paused';
  // A paused store with a change waiting is still approved underneath
  // (`base_review_status`); Unpause stays available for it.
  const approvedBase = (store.base_review_status ?? store.review_status) === 'approved';
  // The backend refuses to put a store live without one of the medical
  // groups we work with (`storefront_medical_group_required`), and the
  // Unpause dialog has no group field: say so before the click.
  const groupMissing = !isRealMedicalGroup(store.medical_group_name, store.medical_group_options);
  const canUnpause = paused && approvedBase && !groupMissing;
  const unpauseReason = !approvedBase
    ? 'Approve the store before it can go live again'
    : groupMissing
      ? 'Set one of the medical groups we work with under Store settings first'
      : null;

  // A refusal from an earlier attempt never shows on the next open.
  function openStatusDialog(next: 'live' | 'paused') {
    setStatusError(null);
    setStatus.reset();
    setSnapshot(expectedState(store));
    setStatusDialog(next);
    void refresh();
  }
  const blocked = unseenChanges > 0 || (statusDialog !== null && movedSince(store, snapshot));
  const blockedNotice = blocked ? (
    <Notice tone="danger">
      This store changed since you opened it. Close this dialog and look over the changes listed
      at the top of the page first.
    </Notice>
  ) : null;
  async function confirmStatus(next: 'live' | 'paused') {
    setStatusError(null);
    try {
      await setStatus.mutateAsync({
        status: next,
        reason: next === 'paused' ? pauseReason.trim() : null,
        ...(snapshot ?? expectedState(store)),
      });
      if (next === 'paused') setPauseReason('');
      setStatusDialog(null);
    } catch (err) {
      setStatusError(refusalMessage(err));
    }
  }

  async function openPreview() {
    setPreviewError(null);
    // Opened inside the click so the popup is allowed; it navigates once
    // the token is back and is cut off from this tab first.
    const tab = window.open('', '_blank');
    try {
      const res = await preview.mutateAsync('live');
      const url = safeHttpUrl(res.url);
      if (!url) throw new Error('The preview link is not a web address.');
      setPreviewLink({ url, expires_at: res.expires_at ?? null });
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      }
    } catch (err) {
      tab?.close();
      setPreviewError(refusalMessage(err));
    }
  }

  return (
    <div className="flex flex-col gap-8 lg:items-end lg:max-w-[60%]">
      <div className="flex flex-wrap items-center gap-8 lg:justify-end">
        <ActionButton onClick={openPreview} disabled={preview.isPending}>
          <Eye size={14} aria-hidden />
          {preview.isPending ? 'Opening…' : 'Preview store'}
        </ActionButton>
        {served ? (
          <a
            href={storeUrl(store.slug)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-8 h-36 px-14 border border-line bg-page type-label-sm text-ink hover:bg-sunken"
          >
            <ExternalLink size={14} aria-hidden /> Open live store
          </a>
        ) : (
          <ActionButton
            disabled
            title={live ? 'Not served: the store uses a reserved address' : 'The store is not live'}
          >
            <ExternalLink size={14} aria-hidden /> Open live store
          </ActionButton>
        )}
        <Link
          to={`/admin/clinical-admin?tab=crm&storefront=${encodeURIComponent(store.slug)}`}
          className="inline-flex items-center gap-8 h-36 px-14 border border-line bg-page type-label-sm text-ink hover:bg-sunken whitespace-nowrap"
          title="This store's customers in the CRM"
        >
          <Users size={14} aria-hidden />
          View customers
          {typeof store.customers_count === 'number' ? ` (${count(store.customers_count)})` : ''}
        </Link>
        <RevertButton store={store} />
        {/* Any store approved before, also while it is back in review: the
            re-approval dialog points here to change the policy. */}
        {!isFirstApproval(store) && <ReviewPolicyControl store={store} />}
        {live && (
          <ActionButton tone="danger" onClick={() => openStatusDialog('paused')}>
            <Pause size={14} aria-hidden /> Pause
          </ActionButton>
        )}
        {paused && (
          <ActionButton
            tone="primary"
            onClick={() => openStatusDialog('live')}
            disabled={!canUnpause}
            title={unpauseReason ?? undefined}
            aria-describedby={unpauseReason ? 'unpause-blocked-reason' : undefined}
          >
            <Play size={14} aria-hidden /> Unpause
          </ActionButton>
        )}
      </div>
      {paused && unpauseReason && (
        <p id="unpause-blocked-reason" className="type-body-sm text-ink-muted lg:text-right">
          Unpause is off: {unpauseReason.toLowerCase()}.
          {approvedBase && groupMissing && (
            <>
              {' '}
              <a href="#store-settings" className="text-ink underline underline-offset-2">
                Store settings
              </a>
            </>
          )}
        </p>
      )}
      {previewLink && (
        <div className="flex flex-wrap items-center gap-8 type-body-sm text-ink-muted lg:justify-end">
          <span>
            Preview link
            {previewLink.expires_at ? ` valid until ${dateTime(previewLink.expires_at)}` : ''}
          </span>
          <a
            href={previewLink.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-ink underline underline-offset-2"
          >
            Open again
          </a>
          <CopyButton value={previewLink.url} label="Copy link" />
        </div>
      )}
      {previewError && <Notice tone="danger">{previewError}</Notice>}

      <ConfirmDialog
        open={statusDialog === 'paused'}
        onOpenChange={(next) => !next && setStatusDialog(null)}
        title="Pause store"
        tone="danger"
        description={`${store.name}'s pages close and it stops taking new orders until you unpause it. Existing orders and plans are not cancelled.`}
        confirmLabel="Pause store"
        pending={setStatus.isPending}
        confirmDisabled={blocked || !pauseReasonOk}
        error={blocked ? null : statusError}
        onConfirm={() => confirmStatus('paused')}
      >
        {blockedNotice}
        <label className="flex flex-col gap-6">
          <span className="type-label-xs text-ink-muted">Reason (required)</span>
          <textarea
            className={textareaClass}
            value={pauseReason}
            onChange={(e) => setPauseReason(e.target.value)}
            maxLength={2000}
            placeholder="e.g. “Paused while the hero photo is replaced.”"
            aria-describedby="pause-reason-help"
            autoFocus
          />
          <span id="pause-reason-help" className="type-body-sm text-ink-muted leading-130">
            The owner reads this in their console (“Your store is paused · Reason: …”). Plain text,
            at least {MIN_REASON} characters; no patient details.
          </span>
        </label>
      </ConfirmDialog>
      <ConfirmDialog
        open={statusDialog === 'live'}
        onOpenChange={(next) => !next && setStatusDialog(null)}
        title="Unpause store"
        description={`${store.name} goes live again: its pages open and it takes orders.`}
        confirmLabel="Unpause"
        pending={setStatus.isPending}
        confirmDisabled={blocked}
        error={blocked ? null : statusError}
        onConfirm={() => confirmStatus('live')}
      >
        {blockedNotice}
      </ConfirmDialog>
    </div>
  );
}

/** The store's review policy: a select in the header; choosing another
 *  policy asks for a confirm (with an optional note, kept in History) and
 *  sends `POST …/review-policy`. */
function ReviewPolicyControl({ store }: { store: AdminStorefrontDetail }) {
  const mutation = useSetStorefrontReviewPolicy(store.id);
  const current = reviewPolicyOf(store);
  const [next, setNext] = useState<StorefrontReviewPolicy | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  function choose(value: string) {
    if (!isReviewPolicy(value) || value === current) return;
    setError(null);
    mutation.reset();
    setNext(value);
  }

  async function confirm() {
    if (!next) return;
    setError(null);
    try {
      await mutation.mutateAsync({ review_policy: next, note: note.trim() || null });
      setNote('');
      setNext(null);
    } catch (err) {
      setError(refusalMessage(err));
    }
  }

  const from = reviewPolicyOption(current);
  const to = next ? reviewPolicyOption(next) : null;
  // Under Every change the owner's product, price and promo edits are saved
  // to their draft; they stay there after a policy change until the draft
  // is published or discarded (the backend keeps them drafted).
  const draftedCommerce = (store.draft?.changes ?? []).some(
    (c) => c.field.startsWith('products.') || c.field.startsWith('promo_codes.'),
  );
  const commerceNote =
    next === 'every_change'
      ? ' From then on the owner’s product, price and promo code edits are saved to their draft and wait with it.'
      : current === 'every_change' && draftedCommerce
        ? ' Product, price and promo code edits already in the owner’s draft stay there until it is published.'
        : '';
  return (
    <>
      <label
        className="inline-flex items-center h-36 border border-line bg-page type-label-sm text-ink hover:bg-sunken focus-within:shadow-focus"
        title={from.explanation}
      >
        <span className="pl-12 pr-6 text-ink-muted whitespace-nowrap">Review policy</span>
        <select
          value={current}
          onChange={(e) => choose(e.target.value)}
          disabled={mutation.isPending}
          className="h-full pl-4 pr-8 bg-transparent type-label-sm text-ink outline-none cursor-pointer disabled:opacity-40"
        >
          {REVIEW_POLICIES.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <ConfirmDialog
        open={next !== null}
        onOpenChange={(open) => !open && setNext(null)}
        title="Change review policy"
        description={
          !to
            ? undefined
            : next === current
              ? `${store.name} uses “${from.label}”. Choose another policy to change it.`
              : `${store.name} goes from “${from.label}” to “${to.label}”. It applies to the owner's next publish; a change already waiting for review stays waiting.${commerceNote}`
        }
        confirmLabel={to ? `Switch to ${to.label}` : 'Switch'}
        pending={mutation.isPending}
        confirmDisabled={next === current}
        error={error}
        onConfirm={confirm}
      >
        {next && (
          <ReviewPolicyChooser
            value={next}
            onChange={setNext}
            current={current}
            legend="New policy"
            name="change-review-policy"
            autoFocus
          />
        )}
        <ReviewPolicySafetyNote />
        <label className="flex flex-col gap-6">
          <span className="type-label-xs text-ink-muted">Note (optional, kept in History)</span>
          <input
            className={inputClass}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            placeholder="e.g. “Two rejected changes this month.”"
          />
        </label>
      </ConfirmDialog>
    </>
  );
}
